import {
    OreButton,
    OreCardContainer,
    OreCardWrapper,
    OreInput,
    OreStack,
    OreToggleButton,
    SvgFilter,
} from '@edenredespana/oreneta'
import { ChangeEvent, KeyboardEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
    OreMultiSelect,
    OreMultiSelectOption,
} from '@/Shared/ui/molecules/OreMultiSelect'
import { storesTranslation } from '@/Store/ui/storesTranslation'
import { useUserData } from '@/User/ui/context/UserDataProvider'

interface TpvFilterProps {
    localOptions: OreMultiSelectOption[]
    onSearch: (search: string, localCodes: string[]) => void
    onClear: (search: string) => void
    isOpen: boolean
}

export const TpvFilter = ({
    localOptions,
    onSearch,
    onClear,
    isOpen,
}: TpvFilterProps): JSX.Element => {
    const { t } = useTranslation()

    const [search, setSearch] = useState<string>('')
    const [selectedLocals, setSelectedLocals] = useState<string[]>([])
    const [filtersOpen, setFiltersOpen] = useState<boolean>(isOpen)
    const { merchant } = useUserData()

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setSearch(event.target.value)
        if (event.target.value === '') {
            onSearch('', selectedLocals)
        }
    }

    const handleEnterKey = (event: KeyboardEvent): void => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            onSearch(search, selectedLocals)
        }
    }

    const clearFilters = (): void => {
        setSelectedLocals([])
        onClear(search)
    }

    return (
        <OreCardContainer radius="top">
            <OreCardWrapper space="large">
                <OreStack placeContent="stretch">
                    <OreStack direction="row" placeContent={merchant?.isIndependent ? "start" : "space-between"} placeItems="center">
                        <OreInput
                            style={{ minWidth: '666px', flexGrow: 1 }}
                            type="search"
                            name="search"
                            id="search"
                            onKeyDown={handleEnterKey}
                            placeholder={merchant?.isIndependent ? 
                                t(storesTranslation.tpvs.filter.search.placeholderIndependent) :
                                t(storesTranslation.tpvs.filter.search.placeholder)}
                            onChange={handleSearchChange}
                            value={search}

                        />
                        {!merchant?.isIndependent && merchant.isParent ? (
                            <OreStack direction='row' placeItems='center'>
                                {!filtersOpen ?
                                    <OreButton
                                        category="primary"
                                        size="small"
                                        onClick={() => onSearch(search, selectedLocals)}
                                    >
                                        {t(storesTranslation.tpvs.filter.search_button)}
                                    </OreButton>
                                    : null}
                                <OreToggleButton
                                    icon={<SvgFilter />}
                                    onClick={() => setFiltersOpen(!filtersOpen)}
                                    title={t(storesTranslation.tpvs.filter.toggle)}
                                >
                                    {t(storesTranslation.tpvs.filter.toggle)}
                                </OreToggleButton>
                            </OreStack>
                        ) :
                            <OreButton
                                category="primary"
                                size="small"
                                onClick={() => onSearch(search, selectedLocals)}
                            >
                                {t(storesTranslation.tpvs.filter.search_button)}
                            </OreButton>}
                    </OreStack>

                    {(filtersOpen && !merchant?.isIndependent && merchant.isParent) ? (
                        <OreCardContainer tone="neutral-100" hasShadow={false}>
                            <OreStack space="large" placeContent="stretch" style={{ padding: '24px' }}>
                                <OreMultiSelect
                                    label={t(storesTranslation.tpvs.filter.local.label)}
                                    placeholder={t(storesTranslation.tpvs.filter.local.placeholder)}
                                    searchPlaceholder={t(
                                        storesTranslation.tpvs.filter.local.searchPlaceholder,
                                    )}
                                    options={localOptions}
                                    value={selectedLocals}
                                    onChange={setSelectedLocals}
                                />
                                <OreStack direction="row">
                                    <OreButton
                                        category="primary"
                                        size="small"
                                        onClick={() => onSearch(search, selectedLocals)}
                                    >
                                        {t(storesTranslation.tpvs.filter.search_button)}
                                    </OreButton>
                                    <OreButton category="secondary" size="small" onClick={clearFilters}>
                                        {t(storesTranslation.tpvs.filter.clear)}
                                    </OreButton>
                                </OreStack>
                            </OreStack>

                        </OreCardContainer>

                    ) : null}
                </OreStack>
            </OreCardWrapper>
        </OreCardContainer>
    )
}
